//* Contrato de GET /payments/ledger/:leaseId (PRD Exigibilidad §7.1). Se transcribe a mano: el
//* backend no publica tipos y el panel no genera cliente. Si cambia allá, cambia aquí.

export type LedgerView = 'PAID' | 'ANTICIPATED' | 'MORA_PENDING' | 'PARTIAL' | 'OVERDUE' | 'PENDING' | 'UPCOMING'
export type LedgerEventType = 'APPLIED' | 'CANCELLED' | 'REGISTERED' | 'INVOICED' | 'REINVOICED' | 'RECONCILED' | 'EDITED' | 'WAIVED'
export type PaymentGrade = 'A' | 'B' | 'C' | 'D'
export type MoraOffBehavior = 'PAUSE' | 'KEEP_CHARGING'

//* Un tramo de la capitalización diaria (PRD §8.1, tabla del ejemplo numérico)
export interface LedgerTramo {
  from: string
  to: string
  days: number
  base: number
  accrued: number
}

export interface LedgerNominal {
  capital: number
  interest: number
  accessoriesAmount: number
  accessoriesInterest: number
  inputIva: number
  rent: number
}

export interface LedgerApplied {
  total: number
  byConcept: Record<string, number>
  ledgerTotal: number
}

export interface LedgerMora {
  rate: number
  days: number
  daily: number
  accrued: number
  paid: number
  waived: number
  balance: number
  //* F27: paused = true solo con la mora apagada y offBehavior = 'PAUSE'; entonces accrued conserva lo guardado y exigible = balance = daily = 0
  paused: boolean
  exigible: number
  asOf: string
  tramos: LedgerTramo[]
}

export interface LedgerApplication {
  applicationId: string
  paymentId: string
  trackingKey: string
  account: string
  paidOn: string
  registeredOn: string
  appliedOn: string
  appliedBy: string
  amount: number
  byConcept: Record<string, number>
  status: string
  cancelledAt?: string
  cancelledBy?: string
  cancelReason?: string
}

export interface LedgerInvoice {
  active: any | null
  history: any[]
  complement: any | null
}

export interface LedgerEvent {
  type: LedgerEventType
  date: string
  user?: string
  detail?: any
}

export interface LedgerDrift {
  paidWithBalance: boolean
  appliedPaymentVsLedger: number
  appliedAmountsVsLedger: Record<string, number>
}

export interface LedgerPeriod {
  _id: string
  n: number
  date: string
  status: string
  nominal: LedgerNominal
  applied: LedgerApplied
  pending: number
  //* Residuo condonado con el editor de saldos (WRITE_OFF_RESIDUAL). No es dinero cobrado: el pendiente ya
  //* lo descuenta, así que se enseña aparte para que «aplicado + condonado = renta» se pueda leer en la fila.
  residualWrittenOff?: number
  outstandingBalance: number
  mora: LedgerMora
  view: LedgerView
  paidAt: string | null
  registeredAt: string | null
  delayDays: number | null
  registrationLagDays: number | null
  applications: LedgerApplication[]
  invoice: LedgerInvoice
  events: LedgerEvent[]
  drift: LedgerDrift
}

//* Evidencia de un gemelo: la misma forma en unapplied[].twins y en detail.twins del 409 (§7.1)
export interface TwinEvidence {
  _id: string
  trackingKey: string
  amount: number
  date: string
  status: string
  pendingToApply: number
  leases: Array<{ folio: string; letters: number[] }>
}

//* Destino de un pago dentro del libro: una aplicación viva o cancelada sobre una letra
export interface PaymentDestination {
  applicationId: string
  leaseId: string
  //* Vacío cuando el dinero se quedó en ESTE arrendamiento; el folio del otro cuando hubo redistribución.
  folio: string
  //* AMORTIZATION_PERIOD · FIRST_PAYMENT · RESIDUAL_PAYMENT: sin esto el enganche se leía «letra 0».
  applicationType: string
  //* `null` cuando no se puede resolver (una aplicación parcial en otro arrendamiento): se omite, no se inventa.
  n: number | null
  //* B-039 · Cada letra a la que se aplicó y cuánto le tocó, saldada o no. Opcional mientras convivan
  //* versiones del API: sin ella se nombra solo `n`.
  letters?: Array<{ n: number | null; amount: number }>
  amount: number
  status: string
  appliedOn: string
  //* Reparto por concepto y quién aplicó: los pinta el cajón en «A dónde fue el dinero» (F6)
  byConcept: Record<string, number>
  appliedBy: string
  cancelledAt?: string
  cancelReason?: string
}

//* Fila de la vista «Por pago» y contenido del cajón (F6, F7)
export interface PaymentRow {
  _id: string
  trackingKey: string
  amount: number
  paymentMethod: string
  account: string
  status: string
  paidOn: string
  registeredOn: string
  registeredBy: string
  registrationLagDays: number
  applied: number
  pendingToApply: number
  pendingToApplyVsLedger: number
  destinations: PaymentDestination[]
  auditTrail: Array<{ action: string; reason?: string; user?: string; date: string }>
  deletedAt: string | null
  //* Viaja en unapplied[] (F16, F18): evidencia de gemelos, la misma forma que detail.twins del 409. Un pago PARTIAL
  //* con aplicación viva y saldo por aplicar está en payments[] Y en unapplied[]: el contenedor fusiona `twins`
  //* al abrir el cajón y lo pasa a Por pago por `twinsById` para el badge «posible duplicado»
  twins?: TwinEvidence[]
}

export interface PaymentQuality {
  paidPeriods: number
  onTime: number
  onTimeRate: number
  avgDelayDays: number
  maxDelayDays: number
  openOverdue: number
  moraPaid: number
  moraWaived: number
  trend: number | null
  avgRegistrationLagDays: number
  grade: PaymentGrade | null
}

export interface LedgerWallet {
  rent: number
  accessories: number
  mora: number
  residual: number
  total: number
}

export interface LedgerTotals {
  overdueAmount: number
  overdueCount: number
  accruedMora: number
  nextDue: { n: number; date: string; amount: number } | null
}

export interface LeaseLedger {
  lease: { _id: string; folio: string; status: string; moratoryRate: number; client: { _id: string; name: string; activeLeases: number } }
  //* con enabled=false y PAUSE la mora materializada se muestra «en pausa» y no entra a pending ni a totals (F27)
  settings: { moraEnabled: boolean; offBehavior: MoraOffBehavior }
  periods: LedgerPeriod[]
  nextToCollect: number | null
  payments: PaymentRow[]
  unapplied: Array<PaymentRow & { twins: TwinEvidence[] }>
  //* null sin letras cubiertas (§8.4): «sin calificación» no es D
  quality: PaymentQuality | null
  wallet: LedgerWallet
  totals: LedgerTotals
}

/*
 * Cómo se llama en pantalla cada estado del libro mayor.
 *
 *! Va por `view`, NUNCA por `status`. `status` es el campo GUARDADO en la letra y en dev hay
 *! arrendamientos enteros con `PAID` guardado y la letra entera sin pagar; `view` es el estado
 *! DERIVADO —vencida, por vencer, parcial— y es el único que corresponde con las cifras de al lado.
 *! El libro mayor de Cobranza pintaba `status` y enseñaba «PAGADO» junto a «Falta $37,228.91» en
 *! rojo, en todas sus letras. Se vio abriendo el diálogo, no compilando.
 *
 * Vive aquí, junto al tipo, porque lo pintan dos pantallas: la pestaña de Exigibilidad y el libro
 * mayor que se abre desde Cartera.
 */
export const LEDGER_VIEW_LABELS: Record<string, string> = {
  PAID: 'PAGADO',
  OVERDUE: 'ATRASADO',
  PENDING: 'PENDIENTE',
  ANTICIPATED: 'ANTICIPADO',
  PARTIAL: 'PARCIAL',
  UPCOMING: 'PENDIENTE',
  MORA_PENDING: 'RENTA CUBIERTA · MORA PENDIENTE',
}
